import { Pause, Play, Pin, PinOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/components/ui/utils'

/**
 * Live-plate scrubber (§3.2 `VideoScrubber.tsx`).
 *
 * A live background is a short loop, and the thing you are annotating —
 * where the ground is, where the tree trunk meets it — must be true for
 * *every* frame. So the scrubber has a **hold frame** toggle: pin a frame,
 * draw against it, then unpin and scrub to check the shape still fits.
 */

export interface VideoScrubberProps {
  /** Seconds. */
  duration: number
  time: number
  onTimeChange: (t: number) => void
  held: boolean
  onHeldChange: (held: boolean) => void
  className?: string
}

export function VideoScrubber({ duration, time, onTimeChange, held, onHeldChange, className }: VideoScrubberProps) {
  const [playing, setPlaying] = useState(false)
  const raf = useRef<number | null>(null)
  const last = useRef<number>(0)

  useEffect(() => {
    if (!playing || held || duration <= 0) return
    last.current = performance.now()
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last.current) / 1000)
      last.current = now
      onTimeChange((time + dt) % duration)
      raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current)
    }
  }, [playing, held, duration, time, onTimeChange])

  if (duration <= 0) return null

  return (
    <div className={cn('flex items-center gap-2 border-t border-line bg-paper px-3 py-1.5', className)}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={playing ? 'Pause the clip' : 'Play the clip'}
        disabled={held}
        onClick={() => setPlaying((p) => !p)}
      >
        {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
      </Button>

      <Slider
        className="flex-1"
        value={[time]}
        min={0}
        max={duration}
        step={0.02}
        onValueChange={(v) => {
          setPlaying(false)
          onTimeChange(v[0] ?? 0)
        }}
        aria-label="Position in the clip"
      />

      <span className="w-20 text-right text-xs tabular-nums text-ink-2">
        {time.toFixed(2)}s / {duration.toFixed(2)}s
      </span>

      <Button variant={held ? 'secondary' : 'ghost'} size="sm" onClick={() => onHeldChange(!held)} aria-pressed={held}>
        {held ? <Pin className="size-4" /> : <PinOff className="size-4" />}
        {held ? 'Frame held' : 'Hold frame'}
      </Button>
    </div>
  )
}
